import { ApiDetails } from '../../../../../Constants';
import { handle401Unauthorized, handle403Forbidden } from '../../../../../utils/authErrorHandler';


/**
 * Agreement Signature API Service
 *
 * Handles all API calls related to agreement signature workflow
 * - Landlord signing
 * - Tenant signing (no auth required)
 * - Signature status checking
 */

/**
 * Landlord signs an agreement
 *
 * @param {number} agreementId - The agreement ID to sign
 * @param {string} signatureData - Base64 encoded signature image (data:image/png;base64,...)
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} API response
 */
export const landlordSignAgreement = async (agreementId, signatureData, token) => {
    try {
        const response = await fetch(ApiDetails + 'pegasus/visionary/agreements/landlord/sign', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'token': token
            },
            body: JSON.stringify({
                agreement_id: agreementId,
                signature_data: signatureData
            })
        });

        // Handle 403 (unauthorized)
        if (response.status === 403) {
            localStorage.clear();
            window.location.href = '/';
            throw new Error('Session expired. Please log in again.');
        }

        const data = await response.json();

        if (response.status === 200 || response.status === 201) {
            return {
                success: true,
                data: data
            };
        } else {
            return {
                success: false,
                error: data.message || data.error || 'Failed to sign agreement'
            };
        }
    } catch (error) {
        console.error('Error signing agreement (landlord):', error);
        return {
            success: false,
            error: error.message || 'Network error. Please try again.'
        };
    }
};

/**
 * Tenant signs an agreement (no authentication required)
 *
 * @param {number} agreementId - The agreement ID to sign
 * @param {number} tenantId - The tenant ID
 * @param {string} signatureData - Base64 encoded signature image (data:image/png;base64,...)
 * @returns {Promise<Object>} API response
 */
export const tenantSignAgreement = async (agreementId, tenantId, signatureData) => {
    try {
        const response = await fetch(ApiDetails + 'pegasus/visionary/agreements/tenant/sign', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                agreement_id: agreementId,
                tenant_id: tenantId,
                signature_data: signatureData
            })
        });

        const data = await response.json();

        if (response.status === 200 || response.status === 201) {
            return {
                success: true,
                data: data
            };
        } else {
            return {
                success: false,
                error: data.message || data.error || 'Failed to sign agreement'
            };
        }
    } catch (error) {
        console.error('Error signing agreement (tenant):', error);
        return {
            success: false,
            error: error.message || 'Network error. Please try again.'
        };
    }
};

/**
 * Get signature status for an agreement
 *
 * @param {number} agreementId - The agreement ID
 * @param {string} token - Authentication token
 * @param {boolean} includeSignatures - Whether to include Base64 signature images (default: true)
 * @returns {Promise<Object>} API response with signature status
 */
export const getSignatureStatus = async (agreementId, token, includeSignatures = true) => {
    try {
        const response = await fetch(ApiDetails + 'pegasus/visionary/agreements/signature/status', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'token': token
            },
            body: JSON.stringify({
                agreement_id: agreementId,
                include_signatures: includeSignatures
            })
        });

        // Handle 403 (unauthorized)
        if (response.status === 403) {
            localStorage.clear();
            window.location.href = '/';
            throw new Error('Session expired. Please log in again.');
        }

        const data = await response.json();

        if (response.status === 200) {
            return {
                success: true,
                data: data
            };
        } else {
            return {
                success: false,
                error: data.message || data.error || 'Failed to fetch signature status'
            };
        }
    } catch (error) {
        console.error('Error fetching signature status:', error);
        return {
            success: false,
            error: error.message || 'Network error. Please try again.'
        };
    }
};

/**
 * Validate signature data format
 *
 * @param {string} signatureData - Base64 signature data to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const validateSignatureData = (signatureData) => {
    if (!signatureData || typeof signatureData !== 'string') {
        return false;
    }

    // Must start with data:image/
    if (!signatureData.startsWith('data:image/')) {
        return false;
    }

    // Must contain base64
    if (!signatureData.includes('base64,')) {
        return false;
    }

    // Check if there's actual data after base64,
    const base64Data = signatureData.split('base64,')[1];
    if (!base64Data || base64Data.length < 10) {
        return false;
    }

    return true;
};

/**
 * Check if user can sign the agreement
 *
 * @param {Object} agreement - The agreement object
 * @param {string} userRole - 'landlord' or 'tenant'
 * @returns {Object} { canSign: boolean, reason: string }
 */
export const canSignAgreement = (agreement, userRole) => {
    if (!agreement) {
        return { canSign: false, reason: 'Agreement not found' };
    }

    // Check if agreement is in correct status
    const validStatuses = ['DRAFT', 'PENDING_SIGNATURE'];
    if (!validStatuses.includes(agreement.status)) {
        return { canSign: false, reason: `Agreement status is ${agreement.status}` };
    }

    // Check if already signed
    if (userRole === 'landlord' && agreement.landlord_signed) {
        return { canSign: false, reason: 'You have already signed this agreement' };
    }

    if (userRole === 'tenant' && agreement.tenant_signed) {
        return { canSign: false, reason: 'You have already signed this agreement' };
    }

    return { canSign: true, reason: '' };
};

/**
 * Get human-readable signature status
 *
 * @param {Object} agreement - The agreement object
 * @returns {string} Status text
 */
export const getSignatureStatusText = (agreement) => {
    if (!agreement) return 'Unknown';

    const landlordSigned = agreement.landlord_signed || false;
    const tenantSigned = agreement.tenant_signed || false;

    if (landlordSigned && tenantSigned) {
        return 'Fully Signed';
    } else if (landlordSigned && !tenantSigned) {
        return 'Awaiting Tenant Signature';
    } else if (!landlordSigned && tenantSigned) {
        return 'Awaiting Landlord Signature';
    } else {
        return 'Not Signed';
    }
};

/**
 * Get next action recommendation
 *
 * @param {Object} agreement - The agreement object
 * @param {string} userRole - 'landlord' or 'tenant'
 * @returns {string} Recommended action
 */
export const getNextAction = (agreement, userRole) => {
    if (!agreement) return '';

    const landlordSigned = agreement.landlord_signed || false;
    const tenantSigned = agreement.tenant_signed || false;

    if (landlordSigned && tenantSigned) {
        return 'Agreement is fully signed. You can download it.';
    }

    if (userRole === 'landlord') {
        if (!landlordSigned) {
            return 'Please sign this agreement.';
        } else if (!tenantSigned) {
            return 'Waiting for tenant to sign. You may send them a notification.';
        }
    } else if (userRole === 'tenant') {
        if (!tenantSigned) {
            return 'Please sign this agreement.';
        } else if (!landlordSigned) {
            return 'Waiting for landlord to sign.';
        }
    }

    return '';
};

/**
 * Get stored landlord signature
 *
 * Retrieves the stored signature for the landlord to display as an option
 * for signing future agreements without re-drawing
 *
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} API response with stored signature data
 */
export const getStoredSignature = async (token) => {
    try {
        const response = await fetch(ApiDetails + 'pegasus/visionary/agreements/landlord/signature', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'token': token
            },
            body: JSON.stringify({})
        });

        // Handle authentication errors
        if (response.status === 401) {
            handle401Unauthorized();
            return {
                success: false,
                error: 'Authentication required. Please log in again.'
            };
        }

        if (response.status === 403) {
            handle403Forbidden();
            return {
                success: false,
                error: 'You do not have permission to access this signature.'
            };
        }

        const data = await response.json();

        if (response.status === 200) {
            return {
                success: true,
                hasSignature: data.has_signature || false,
                signatureData: data.signature_data || null,
                signatureId: data.signature_id || null,
                message: data.message || ''
            };
        } else {
            return {
                success: false,
                error: data.message || data.error || 'Failed to fetch stored signature'
            };
        }
    } catch (error) {
        console.error('Error fetching stored signature:', error);
        return {
            success: false,
            error: error.message || 'Network error. Please try again.',
            hasSignature: false
        };
    }
};

/**
 * Sign agreement using stored signature
 *
 * Signs an agreement using the landlord's previously stored signature
 * without requiring re-drawing
 *
 * @param {number} agreementId - The agreement ID to sign
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} API response
 */
export const signAgreementWithStoredSignature = async (agreementId, token) => {
    try {
        // Validate input
        if (!agreementId || typeof agreementId !== 'number') {
            return {
                success: false,
                error: 'Invalid agreement ID'
            };
        }

        const response = await fetch(ApiDetails + 'pegasus/visionary/agreements/landlord/sign-with-stored', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'token': token
            },
            body: JSON.stringify({
                agreement_id: agreementId
            })
        });

        // Handle authentication errors
        if (response.status === 401) {
            handle401Unauthorized();
            return {
                success: false,
                error: 'Authentication required. Please log in again.'
            };
        }

        if (response.status === 403) {
            handle403Forbidden();
            return {
                success: false,
                error: 'You do not have permission to sign this agreement.'
            };
        }

        const data = await response.json();

        if (response.status === 200 || response.status === 201) {
            return {
                success: true,
                data: data,
                agreementId: data.agreement_id,
                status: data.status || 'SIGNED',
                fullySignedByLandlord: data.fully_signed || false,
                message: data.message || 'Agreement signed successfully'
            };
        } else {
            return {
                success: false,
                error: data.message || data.error || 'Failed to sign agreement with stored signature'
            };
        }
    } catch (error) {
        console.error('Error signing agreement with stored signature:', error);
        return {
            success: false,
            error: error.message || 'Network error. Please try again.'
        };
    }
};

/**
 * Get all stored signatures for the landlord
 *
 * Retrieves a list of all signatures stored by the landlord
 * Used for the signature selector UI to display options
 *
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} API response with list of signatures
 */
export const getAllStoredSignatures = async (token) => {
    try {
        const response = await fetch(ApiDetails + 'pegasus/visionary/agreements/landlord/signature', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'token': token
            },
            body: JSON.stringify({})
        });

        // Handle authentication errors
        if (response.status === 401) {
            handle401Unauthorized();
            return {
                success: false,
                error: 'Authentication required. Please log in again.',
                signatures: []
            };
        }

        if (response.status === 403) {
            handle403Forbidden();
            return {
                success: false,
                error: 'You do not have permission to access these signatures.',
                signatures: []
            };
        }

        const data = await response.json();

        if (response.status === 200) {
            return {
                success: true,
                signatures: data.signatures || [],
                count: (data.signatures || []).length,
                message: data.message || ''
            };
        } else {
            return {
                success: false,
                error: data.message || data.error || 'Failed to fetch signatures',
                signatures: []
            };
        }
    } catch (error) {
        console.error('Error fetching all signatures:', error);
        return {
            success: false,
            error: error.message || 'Network error. Please try again.',
            signatures: []
        };
    }
};

/**
 * Save a new signature
 *
 * Saves a new signature for the landlord
 * This signature will be available for all future agreements
 *
 * @param {string} signatureData - Base64 encoded signature image
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} API response with saved signature info
 */
export const saveNewSignature = async (signatureData, token) => {
    try {
        // Validate signature data
        if (!validateSignatureData(signatureData)) {
            return {
                success: false,
                error: 'Invalid signature format'
            };
        }

        const response = await fetch(ApiDetails + 'pegasus/visionary/agreements/landlord/signature/save', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'token': token
            },
            body: JSON.stringify({
                signature_data: signatureData
            })
        });

        // Handle authentication errors
        if (response.status === 401) {
            handle401Unauthorized();
            return {
                success: false,
                error: 'Authentication required. Please log in again.'
            };
        }

        if (response.status === 403) {
            handle403Forbidden();
            return {
                success: false,
                error: 'You do not have permission to save signatures.'
            };
        }

        const data = await response.json();

        if (response.status === 200 || response.status === 201) {
            return {
                success: true,
                signatureId: data.signature_id,
                signatureData: signatureData,
                message: data.message || 'Signature saved successfully'
            };
        } else {
            return {
                success: false,
                error: data.message || data.error || 'Failed to save signature'
            };
        }
    } catch (error) {
        console.error('Error saving signature:', error);
        return {
            success: false,
            error: error.message || 'Network error. Please try again.'
        };
    }
};

/**
 * Delete a stored signature
 *
 * Removes a signature from storage
 * Cannot be undone
 *
 * @param {number} signatureId - The signature ID to delete
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} API response
 */
export const deleteStoredSignature = async (signatureId, token) => {
    try {
        if (!signatureId || typeof signatureId !== 'number') {
            return {
                success: false,
                error: 'Invalid signature ID'
            };
        }

        const response = await fetch(ApiDetails + 'pegasus/visionary/agreements/landlord/signature/delete', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'token': token
            },
            body: JSON.stringify({
                signature_id: signatureId
            })
        });

        // Handle authentication errors
        if (response.status === 401) {
            handle401Unauthorized();
            return {
                success: false,
                error: 'Authentication required. Please log in again.'
            };
        }

        if (response.status === 403) {
            handle403Forbidden();
            return {
                success: false,
                error: 'You do not have permission to delete signatures.'
            };
        }

        const data = await response.json();

        if (response.status === 200) {
            return {
                success: true,
                message: data.message || 'Signature deleted successfully'
            };
        } else {
            return {
                success: false,
                error: data.message || data.error || 'Failed to delete signature'
            };
        }
    } catch (error) {
        console.error('Error deleting signature:', error);
        return {
            success: false,
            error: error.message || 'Network error. Please try again.'
        };
    }
};

/**
 * Sign multiple agreements with a selected signature
 *
 * Bulk sign multiple agreements using the same signature
 * More efficient than signing one-by-one
 *
 * @param {number[]} agreementIds - Array of agreement IDs to sign
 * @param {number} signatureId - The signature ID to use
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} API response with results for each agreement
 */
export const signMultipleAgreements = async (agreementIds, signatureId, token) => {
    try {
        // Validate inputs
        if (!Array.isArray(agreementIds) || agreementIds.length === 0) {
            return {
                success: false,
                error: 'Invalid agreement IDs',
                results: []
            };
        }

        if (!signatureId || typeof signatureId !== 'number') {
            return {
                success: false,
                error: 'Invalid signature ID',
                results: []
            };
        }

        const response = await fetch(ApiDetails + 'pegasus/visionary/agreements/landlord/sign-multiple', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'token': token
            },
            body: JSON.stringify({
                agreement_ids: agreementIds,
                signature_id: signatureId
            })
        });

        // Handle authentication errors
        if (response.status === 401) {
            handle401Unauthorized();
            return {
                success: false,
                error: 'Authentication required. Please log in again.',
                results: []
            };
        }

        if (response.status === 403) {
            handle403Forbidden();
            return {
                success: false,
                error: 'You do not have permission to sign these agreements.',
                results: []
            };
        }

        const data = await response.json();

        if (response.status === 200) {
            return {
                success: true,
                results: data.results || [],
                successCount: data.success_count || 0,
                failureCount: data.failure_count || 0,
                message: data.message || 'Agreements signed successfully'
            };
        } else {
            return {
                success: false,
                error: data.message || data.error || 'Failed to sign agreements',
                results: data.results || []
            };
        }
    } catch (error) {
        console.error('Error signing multiple agreements:', error);
        return {
            success: false,
            error: error.message || 'Network error. Please try again.',
            results: []
        };
    }
};
